/* global ga */

import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import md5 from 'md5';

import SoyuzAnalytics from '@spotahome/soyuz-analytics';

import { useCookie } from '../../..';

import getGqlClient from '../clients/graphql/marketplace';

import {
  handleSuccessToast,
  handleErrorToast
} from '../components/utils/loginToast';

const UserInfoContext = React.createContext({
  isUserLogged: false,
  userInfo: null
});

const QUERY_USER_INFO = gql`
  query UserInfo {
    userInfo {
      id
      email
      firstLogin
      fullname
      isActivatedByEmail
      createdAt
      validationActivationSuccess
    }
  }
`;

const getUserInfo = client => () =>
  client.query({
    query: QUERY_USER_INFO,
    fetchPolicy: 'network-only'
  });

const setUserId = userInfo => {
  const { email } = userInfo || {};
  if (typeof ga !== 'undefined' && email) {
    const userId = md5(email);
    ga('set', 'userId', userId);
    SoyuzAnalytics.sendGA4Event('set', { user_id: userId });
  }
};

export const UserInfoProvider = ({
  children,
  isUserLogged,
  initialUserInfo
}) => {
  const [userInfo, setUserInfo] = useState(initialUserInfo);
  const { getCookie, removeCookie } = useCookie();

  const handleUserLoginEvent = () => {
    const userLoginMethod = getCookie('sah-ui-login');

    if (userLoginMethod) {
      SoyuzAnalytics.sendGA4Event('login', { method: userLoginMethod });
      removeCookie('sah-ui-login');
    }
  };

  useEffect(() => {
    handleUserLoginEvent();
  }, []);

  useEffect(() => {
    const requestUserInfo = async () => {
      try {
        const response = await getUserInfo(getGqlClient())();
        setUserInfo(response.data.userInfo);
        setUserId(response.data.userInfo);
      } catch (error) {
        console.log(error);
        handleErrorToast(error);
      }
    };

    if (!isUserLogged || !initialUserInfo) {
      requestUserInfo();
    } else {
      // Is logged
      handleSuccessToast(
        userInfo.firstLogin,
        userInfo.validationActivationSuccess
      );
    }
  }, []);

  return (
    <UserInfoContext.Provider
      value={{
        isUserLogged: isUserLogged && !!userInfo?.id,
        userInfo
      }}
    >
      {children}
    </UserInfoContext.Provider>
  );
};

UserInfoProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired,
  isUserLogged: PropTypes.bool,
  initialUserInfo: PropTypes.shape({
    id: PropTypes.string,
    email: PropTypes.string,
    firstLogin: PropTypes.bool,
    fullname: PropTypes.string,
    isActivatedByEmail: PropTypes.bool,
    createdAt: PropTypes.string,
    validationActivationSuccess: PropTypes.bool
  })
};

UserInfoProvider.defaultProps = {
  isUserLogged: false,
  initialUserInfo: null
};

export const UserInfoConsumer = UserInfoContext.Consumer;

export const useUserInfoContext = () => useContext(UserInfoContext) || {};

export const useIsUserLogged = () => {
  const userInfoContext = useUserInfoContext();

  return userInfoContext.isUserLogged;
};

export default {
  UserInfoProvider,
  UserInfoConsumer,
  useUserInfoContext,
  useIsUserLogged
};
